import React, { useEffect, useState, useCallback } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Typography,
    OutlinedInput,
    InputAdornment,
    Paper
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { IconSearch } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash.debounce';
import api from '../../../../services/api';

const useStyles = makeStyles((theme) => ({
    searchControl: {
        width: '434px',
        marginLeft: '16px',
        marginBottom: '8px',
        background: '#fff',
        [theme.breakpoints.down('lg')]: { width: '100%' }
    },
    popperResults: {
        position: 'absolute',
        zIndex: 1200,
        width: '434px',
        marginLeft: '16px',
        backgroundColor: '#fff',
        boxShadow: theme.shadows[3],
        maxHeight: '250px',
        overflowY: 'auto',
        borderRadius: '8px',
        [theme.breakpoints.down('lg')]: { width: '100%' }
    },
    listItemHover: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            cursor: 'pointer'
        }
    }
}));

const ViewUsers = () => {
    const [users, setUsers] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [appId, setAppId] = useState(null);

    const classes = useStyles();
    const history = useHistory();

    //  pega app padrão
    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            setAppId(response.data.app.id);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        pegar_app_padrao();
    }, []);

    // 🔥 só lista quando tiver appId
    useEffect(() => {
        if (!appId) return;
        listarUsuarios();
    }, [appId]);

    const listarUsuarios = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/users/`);
            setUsers(response.data.users || response.data);
        } catch (error) {
            console.error(error);
            setStatus({
                type: 'error',
                message: 'Erro ao listar clientes.'
            });
        }
    };

    const handleDeleteMany = (userIds) => {
        console.log('Deletar usuários:', userIds);
        setSelectedUsers([]);
    };

    const handleOpenUser = (user) => {
        setShowResults(false);
        history.push(`/dashboard/config/users/edit/${user.id}`);
    };

    // debounce DEPENDENDO do appId
    const handleSearch = useCallback(
        debounce(async (value, currentAppId) => {
            if (!value || !currentAppId) {
                setSearchResults([]);
                setShowResults(false);
                return;
            }

            try {
                const response = await api.get(`v1/apps/${currentAppId}/users/?search=${encodeURIComponent(value)}`);

                setSearchResults(response.data.users || response.data);
                setShowResults(true);
            } catch (error) {
                console.error('Erro na busca:', error);
                setStatus({
                    type: 'error',
                    message: 'Erro ao buscar clientes'
                });
            }
        }, 400),
        []
    );

    const handleChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (!appId) return;

        handleSearch(value, appId);
    };

    // Debounce utilitário para evitar spam na API
    function debounce(func, delay) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => func(...args), delay);
        };
    }

    const handleOpenOrganization = (organization) => {
        setShowResults(false);
        history.push(`/dashboard/config/organizations/edit/${organization.id}`);
    };

    return (
        <Box sx={{ position: 'relative' }}>
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
                Clientes
            </Typography>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            {/* Barra de busca */}
            <OutlinedInput
                className={classes.searchControl}
                id="input-search-header"
                value={searchTerm}
                onChange={handleChange}
                placeholder="Buscar por nome, CNPJ ou CPF..."
                startAdornment={
                    <InputAdornment position="start">
                        <IconSearch size="1.2rem" color="#999" />
                    </InputAdornment>
                }
            />

            {/* Resultados da busca em tempo real */}
            {showResults && searchResults.length > 0 && (
                <Paper className={classes.popperResults}>
                    <List dense>
                        {searchResults.map((user) => (
                            <ListItem key={user.id} className={classes.listItemHover} onClick={() => handleOpenUser(user)}>
                                <ListItemText
                                    primary={user.first_name}
                                    secondary={`${user.email || user.cpf || 'Sem documento'} — Organização: ${
                                        user.organization || 'Sem organização'
                                    }`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            <Card sx={{ mt: 4 }}>
                <CardContent>
                    {/* Barra superior com ações em massa */}
                    {selectedUsers.length > 0 && (
                        <Box
                            sx={{
                                p: 2,
                                mb: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: '#f8f8f8',
                                borderRadius: 1
                            }}
                        >
                            <Typography>{selectedUsers.length} clientes selecionados</Typography>

                            <Button variant="contained" color="error" onClick={() => handleDeleteMany(selectedUsers)}>
                                Excluir Selecionados
                            </Button>
                        </Box>
                    )}

                    {/* Cabeçalho */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '50px 60px 1fr 1.5fr 1fr 1fr 1fr 1fr 0.1px',
                            fontWeight: 'bold',
                            pb: 1,
                            borderBottom: '2px solid #eee'
                        }}
                    >
                        <Checkbox
                            checked={selectedUsers.length === users.length && users.length > 0}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedUsers(users.map((u) => u.id));
                                } else {
                                    setSelectedUsers([]);
                                }
                            }}
                        />

                        <Typography></Typography>
                        <Typography>Nome</Typography>
                        <Typography>Email</Typography>
                        <Typography>Telefone</Typography>
                        {/* <Typography>Permissão</Typography> */}
                        <Typography>Organização</Typography>
                        <Typography>Unidade</Typography>
                        <Typography>LOGO</Typography>
                        <Typography></Typography>
                    </Box>

                    {/* Linhas da tabela */}
                    {users.map((user) => (
                        <Box
                            key={user.id}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '50px 60px 1fr 1.5fr 1fr 1fr 1fr 1fr 0.1px',
                                borderBottom: '1px solid #f0f0f0',
                                py: 1,
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}
                            onClick={(e) => {
                                if (e.target.tagName !== 'INPUT' && !e.target.dataset.org) {
                                    handleOpenUser(user);
                                }
                            }}
                        >
                            {/* Checkbox */}
                            <Checkbox
                                checked={selectedUsers.includes(user.id)}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    if (e.target.checked) {
                                        setSelectedUsers([...selectedUsers, user.id]);
                                    } else {
                                        setSelectedUsers(selectedUsers.filter((id) => id !== user.id));
                                    }
                                }}
                            />

                            {/* Avatar do usuário */}
                            <Box>
                                <img
                                    src={user.avatar || '/logo_user.jpg'}
                                    alt="avatar"
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>

                            {/* Nome */}
                            <Typography>
                                {user.first_name} {user.last_name}
                            </Typography>

                            {/* Email */}
                            <Typography>{user.email || '—'}</Typography>

                            {/* Telefone */}
                            <Typography>{user.phone || '—'}</Typography>

                            {/* Permissão */}
                            {/* {user.role === 'customer' && <Typography>Usuário Final</Typography>}
                            {user.role === 'admin' && <Typography>Administrador</Typography>}
                            {user.role === 'agent' && <Typography>Membro da equipe</Typography>} */}

                            {/* Organização (CLICÁVEL) */}
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (user.organization) {
                                        handleOpenOrganization({ id: user.organization });
                                    }
                                }}
                                data-org
                                sx={{
                                    cursor: user.organization ? 'pointer' : 'default',
                                    color: user.organization ? '#1976d2' : '#555',
                                    textDecoration: user.organization ? 'underline' : 'none'
                                }}
                            >
                                {user.organization_name || '—'}
                            </Box>

                            {/* Unidade/Estabelecimento */}
                            <Typography>{user.establishment_name || '—'}</Typography>

                            {/* Avatar da organização */}
                            <Box>
                                <img
                                    src={'/company.png'}
                                    alt="organization-logo"
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}
                </CardContent>
            </Card>
            {/* Lista completa (abaixo da busca) */}
            {/* <Card sx={{ mt: 4 }}>
                <CardContent>
                    <List>
                        <ListItemText variant="h6" gutterBottom style={{ fontWeight: 'bold', padding: '16px', alignContent: 'left' }}>
                            Nome
                        </ListItemText>
                        <ListItemText variant="h6" gutterBottom style={{ fontWeight: 'bold', padding: '16px', alignContent: 'left' }}>
                            Email
                        </ListItemText>
                        <ListItemText variant="h6" gutterBottom style={{ fontWeight: 'bold', padding: '16px', alignContent: 'left' }}>
                            Telefone
                        </ListItemText>
                        {users.map((user) => (
                            <ListItem key={user.id} button onClick={() => handleOpenUser(user)}>
                                <ListItemText
                                    primary={user.name}
                                    secondary={
                                        <Box>
                                            <Typography variant="body2" color="textSecondary">
                                                {user.first_name || user.username || 'Sem Nome'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {user.email || 'Sem Email'}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card> */}
        </Box>
    );
};

export default ViewUsers;
